import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Zap, Check, ArrowRight } from 'lucide-react';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { useNavigate } from 'react-router-dom';

export const UpgradeModal = ({ isOpen, onClose, feature, currentTier = 'free' }) => {
  const navigate = useNavigate();

  const featureMessages = {
    vehicles: {
      title: 'Vehicle Limit Reached',
      message: 'Upgrade to add more vehicles to your account',
      icon: '🚗'
    },
    ai_scan: {
      title: 'AI Scan Limit Reached',
      message: 'Upgrade for unlimited AI document scanning',
      icon: '📄'
    },
    default: {
      title: 'Upgrade Required',
      message: 'Unlock premium features with an upgrade',
      icon: '⚡'
    }
  };

  const currentMessage = featureMessages[feature] || featureMessages.default;

  const plans = [
    {
      name: 'Premium',
      price: '£4.99',
      interval: '/month',
      features: ['1 vehicle', 'Unlimited AI scans', 'All premium features'],
      recommended: true
    },
    {
      name: 'Family',
      price: '£7.99',
      interval: '/month',
      features: ['5 vehicles', '5 users', 'Unlimited AI scans'],
      recommended: false
    },
    {
      name: 'Fleet',
      price: '£49.99',
      interval: '/month',
      features: ['20 vehicles', '20 users', 'Dedicated support'],
      recommended: false
    }
  ];

  const handleUpgrade = () => {
    onClose();
    navigate('/pricing');
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50"
            onClick={onClose}
            data-testid="upgrade-modal-backdrop"
          />

          {/* Modal */}
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              transition={{ type: 'spring', duration: 0.3 }}
              className="w-full max-w-2xl"
              data-testid="upgrade-modal"
            >
              <Card className="relative p-6 sm:p-8">
                {/* Close Button */}
                <button
                  onClick={onClose}
                  className="absolute top-4 right-4 p-2 rounded-lg hover:bg-muted transition-colors"
                  data-testid="upgrade-modal-close"
                >
                  <X className="h-5 w-5" />
                </button>

                {/* Header */}
                <div className="text-center mb-6">
                  <div className="text-5xl mb-3">{currentMessage.icon}</div>
                  <h2 className="text-2xl font-semibold mb-2">{currentMessage.title}</h2>
                  <p className="text-muted-foreground">{currentMessage.message}</p>
                </div>

                {/* Plan Cards */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
                  {plans.map((plan) => (
                    <div
                      key={plan.name}
                      className={`relative p-4 rounded-lg border ${
                        plan.recommended
                          ? 'border-primary bg-primary/5'
                          : 'border-border bg-muted/30'
                      }`}
                    >
                      {plan.recommended && (
                        <Badge className="absolute -top-2 left-1/2 -translate-x-1/2 bg-primary text-primary-foreground text-xs">
                          Recommended
                        </Badge>
                      )}

                      <div className="text-center mb-3">
                        <h3 className="font-semibold text-lg">{plan.name}</h3>
                        <div className="flex items-baseline justify-center gap-1 mt-1">
                          <span className="text-2xl font-bold">{plan.price}</span>
                          <span className="text-sm text-muted-foreground">{plan.interval}</span>
                        </div>
                      </div>

                      <ul className="space-y-2">
                        {plan.features.map((feature, idx) => (
                          <li key={idx} className="flex items-start gap-2 text-sm">
                            <Check className="w-4 h-4 mt-0.5 text-green-600 flex-shrink-0" />
                            <span>{feature}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>

                {/* CTA */}
                <div className="flex flex-col sm:flex-row gap-3">
                  <Button
                    onClick={handleUpgrade}
                    className="flex-1 flex items-center justify-center gap-2"
                    data-testid="upgrade-modal-cta"
                  >
                    <Zap className="h-4 w-4" />
                    View All Plans
                    <ArrowRight className="h-4 w-4" />
                  </Button>
                  <Button
                    onClick={onClose}
                    variant="outline"
                    className="sm:w-auto"
                    data-testid="upgrade-modal-cancel"
                  >
                    Maybe Later
                  </Button>
                </div>

                {/* Footer Note */}
                <p className="text-xs text-center text-muted-foreground mt-4">
                  All plans include secure data storage and can be cancelled anytime.
                </p>
              </Card>
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>
  );
};

export default UpgradeModal;
